import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { invalidateQuery, URLKeys } from 'noddi-async';
import { FieldErrorMessage, NoddiButton, NoddiInput, noddiToast } from 'noddi-ui-web';
import { isPhoneNumberValid } from 'noddi-util';
import { useAuthContext } from 'noddi-web';
import { useState } from 'react';
import { normalizeContactValue, useContactVerification } from '../../../hooks/useContactVerification';

type Props = {
  invalidPhoneDisplay: string;
  isLandline: boolean;
};

/** Inline recovery for an invalid phone number on the signed-in customer account. */
export const UpdateAccountPhoneNumberForm = ({ invalidPhoneDisplay, isLandline }: Props) => {
  const { userData } = useAuthContext();
  const [value, setValue] = useState(userData?.user.phoneNumber ?? '');
  const [verificationCode, setVerificationCode] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const {
    isSendingPhoneNumberVerification,
    isUpdatingContact,
    requiresVerification,
    sendVerification,
    submitContactUpdate
  } = useContactVerification();

  const handleSave = async () => {
    setValidationError(null);
    const phoneNumber = normalizeContactValue('phoneNumber', value);
    if (!isPhoneNumberValid(phoneNumber)) {
      setValidationError(t`Phone number is not valid`);
      return;
    }
    // The prefilled value is the unsupported number itself; the backend rejects an unchanged save.
    if (phoneNumber === userData?.user.phoneNumber) {
      setValidationError(t`Enter a different mobile number`);
      return;
    }

    try {
      if (requiresVerification && phoneNumber !== userData!.user.phoneNumber && !verificationCode.trim()) {
        setValidationError(t`Enter the code sent by SMS`);
        return;
      }
      await submitContactUpdate('phoneNumber', phoneNumber, verificationCode);
      await invalidateQuery({ urlKey: URLKeys.getBookingProposalBySlug });
      noddiToast.success(t`Account phone number saved`);
    } catch {
      // Mutation error toast is handled by noddiAsync; keep the input for retry.
    }
  };

  const handleSendVerification = async () => {
    const phoneNumber = normalizeContactValue('phoneNumber', value);
    if (!isPhoneNumberValid(phoneNumber)) {
      setValidationError(t`Phone number is not valid`);
      return;
    }
    sendVerification('phoneNumber', phoneNumber, () => noddiToast.success(t`Confirmation code sent`));
  };

  if (!userData) {
    return null;
  }

  return (
    <div className='flex flex-col gap-2 rounded-lg border border-border p-4'>
      <div className='flex flex-col gap-1'>
        <h3 className='font-semibold text-primary'>
          <Trans>Update phone number on your account</Trans>
        </h3>
        <span className='text-sm text-muted-foreground'>
          {isLandline ? (
            <Trans>
              The phone number on your account ({invalidPhoneDisplay}) is a landline, which is not supported. Enter a
              mobile number for this account to continue with this booking.
            </Trans>
          ) : (
            <Trans>
              The phone number on your account ({invalidPhoneDisplay}) is not a supported mobile number. Enter a mobile
              number for this account to continue with this booking.
            </Trans>
          )}
        </span>
      </div>

      <NoddiInput
        type='tel'
        name='account-phone-number'
        label={t`Phone number on your account`}
        value={value}
        onChange={(event) => {
          setValue(event.target.value);
          setVerificationCode('');
          setValidationError(null);
        }}
      />
      {requiresVerification && value.trim() !== userData.user.phoneNumber && (
        <>
          <NoddiButton
            variant='outline'
            loading={isSendingPhoneNumberVerification}
            disabled={isSendingPhoneNumberVerification}
            onClick={() => void handleSendVerification()}
          >
            <Trans>Send SMS code</Trans>
          </NoddiButton>
          <NoddiInput
            name='account-phone-number-verification-code'
            label={t`SMS verification code`}
            value={verificationCode}
            onChange={(event) => {
              setVerificationCode(event.target.value);
              setValidationError(null);
            }}
          />
        </>
      )}
      {validationError && <FieldErrorMessage errorMessage={validationError} />}

      <NoddiButton
        variant='primary'
        loading={isUpdatingContact}
        disabled={isUpdatingContact}
        onClick={() => void handleSave()}
      >
        <Trans>Save account phone number</Trans>
      </NoddiButton>
    </div>
  );
};
